/**
 * Bloomyer Featured Collection — Tab switching + carousel dots
 */

if (!customElements.get('bf-featured')) {
  class BfFeatured extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('.bf-tab');
      this.panels = this.querySelectorAll('.bf-panel');
      this.dotContainers = this.querySelectorAll('.bf-dots');
    }

    connectedCallback() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => this.switchTab(e));
      });

      // Observe scroll for dot update
      this.panels.forEach((panel) => {
        const track = panel.querySelector('.bf-cards');
        if (track) {
          track.addEventListener('scroll', () => this.updateDots(panel), { passive: true });
        }
      });

      // Build dots based on the actual number of visible cards (responsive)
      this.buildAllDots();

      // Rebuild on resize (desktop = 3/view, mobile = 1/view)
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.buildAllDots(), 150);
      });
    }

    buildAllDots() {
      this.panels.forEach((panel) => this.buildDots(panel));
    }

    getVisibleCards(track, cards) {
      const cardWidth = cards[0].offsetWidth + 16; // card width + gap
      const visible = Math.floor(track.offsetWidth / cardWidth) || 1;
      return Math.max(1, visible);
    }

    buildDots(panel) {
      const track = panel.querySelector('.bf-cards');
      if (!track) return;

      const cards = track.querySelectorAll('.bf-card');
      let dotsEl = panel.querySelector('.bf-dots');

      // Ensure a dots container exists
      if (!dotsEl) {
        dotsEl = document.createElement('div');
        dotsEl.className = 'bf-dots';
        track.insertAdjacentElement('afterend', dotsEl);
      }

      if (cards.length === 0) {
        dotsEl.style.display = 'none';
        return;
      }

      const visibleCards = this.getVisibleCards(track, cards);
      const totalPages = Math.ceil(cards.length / visibleCards);

      // Hide dots when everything fits in a single view
      if (totalPages <= 1) {
        dotsEl.innerHTML = '';
        dotsEl.style.display = 'none';
        return;
      }

      // (Re)build the dots to match the page count
      dotsEl.style.display = '';
      dotsEl.innerHTML = '';
      for (let i = 0; i < totalPages; i += 1) {
        const dot = document.createElement('button');
        dot.className = 'bf-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('data-page', String(i));
        dot.setAttribute('aria-label', 'Page ' + (i + 1));
        dot.addEventListener('click', (e) => this.scrollToPage(e));
        dotsEl.appendChild(dot);
      }

      this.updateDots(panel);
    }

    switchTab(e) {
      const target = e.currentTarget;
      const tabId = target.getAttribute('data-tab');

      // Update tabs
      this.tabs.forEach((t) => t.setAttribute('aria-selected', 'false'));
      target.setAttribute('aria-selected', 'true');

      // Update panels
      this.panels.forEach((p) => {
        p.classList.toggle('active', p.getAttribute('data-tab') === tabId);
      });

      // Rebuild dots after panel switch (offsetWidth is only valid once visible)
      const activePanel = this.querySelector(`.bf-panel[data-tab="${tabId}"]`);
      if (activePanel) this.buildDots(activePanel);
    }

    updateDots(panel) {
      const track = panel.querySelector('.bf-cards');
      const dots = panel.querySelectorAll('.bf-dot');
      if (!track || dots.length === 0) return;

      const cards = track.querySelectorAll('.bf-card');
      if (cards.length === 0) return;

      const cardWidth = cards[0].offsetWidth + 16; // card width + gap
      const visibleCards = this.getVisibleCards(track, cards);
      const currentPage = Math.round(track.scrollLeft / (cardWidth * visibleCards));

      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === currentPage);
      });
    }

    scrollToPage(e) {
      const dot = e.currentTarget;
      const pageIndex = parseInt(dot.getAttribute('data-page'), 10);
      const panel = dot.closest('.bf-panel') || dot.closest('.bf-cards-area');
      const track = panel ? panel.querySelector('.bf-cards') : null;
      if (!track) return;

      const cards = track.querySelectorAll('.bf-card');
      if (cards.length === 0) return;

      const cardWidth = cards[0].offsetWidth + 16;
      const trackWidth = track.offsetWidth;
      const visibleCards = Math.floor(trackWidth / cardWidth) || 1;

      track.scrollTo({
        left: pageIndex * visibleCards * cardWidth,
        behavior: 'smooth',
      });
    }
  }

  customElements.define('bf-featured', BfFeatured);
}
